import { useTranslation } from 'react-i18next';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { Button, Image, Table } from 'antd';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { ColumnProps } from 'antd/es/table';
import { useUIDispatchContext } from 'providers/UI';
import { deleteSubscriptionPlan } from 'services/api/subscriptions';
import { SubscriptionListRow } from 'types/api/subscriptions';

import useSubscriptionPlans from 'hooks/useSubscriptionPlans';
import { Private } from 'components/shared';
import { ActionMenu, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import { getFileUrl } from 'utils/file';

type ColumnType = SubscriptionListRow;

const SubscriptionPlans = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggleModal } = useUIDispatchContext();
  const { results, loading, refetch } = useSubscriptionPlans();

  const onDeletePlan = useCallback(
    (id: number) => {
      toggleModal('confirm', true, {
        title: t('subscription.delete_subscription_plan'),
        async onConfirm() {
          try {
            await deleteSubscriptionPlan(id);
            refetch();
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    [refetch, t, toggleModal]
  );

  const columns: ColumnProps<ColumnType>[] = useMemo(
    () => [
      {
        title: t('subscription.image'),
        dataIndex: 'icon',
        key: 'icon',
        render: (icon: string) => (
          <Image
            width={30}
            height={30}
            src={getFileUrl(icon)}
            preview={false}
          />
        )
      },
      {
        title: t('subscription.plan_name'),
        dataIndex: 'name',
        key: 'name',
        render: (name: Record<string, string>) => name?.en ?? ''
      },
      {
        title: '',
        dataIndex: 'action',
        key: 'action',
        align: 'right',
        render: (_, { id }) => (
          <ActionMenu
            data={[
              {
                title: t('table.edit'),
                permission: {
                  route: PERMISSION_ROUTES.faq,
                  action: PERMISSION_ACTIONS.update
                },
                action() {
                  navigate(`/subscriptions/plans/${id}`);
                },
                menuProps: {
                  style: {
                    color: 'var(--primary)'
                  }
                }
              },
              {
                title: t('table.delete'),
                permission: {
                  route: PERMISSION_ROUTES.faq,
                  action: PERMISSION_ACTIONS.delete
                },
                action() {
                  onDeletePlan(id);
                },
                menuProps: {
                  style: {
                    color: 'var(--danger-color)'
                  }
                }
              }
            ]}
            placement="bottomRight"
            icon={<EllipsisOutlined />}
          />
        )
      }
    ],
    [navigate, onDeletePlan, t]
  );

  return (
    <PageWrapper
      title={t('subscription.subscription_plans')}
      color="white"
      extra={
        results?.length
          ? []
          : [
              <Private
                key="add-blog-button"
                route={PERMISSION_ROUTES.blog}
                action={PERMISSION_ACTIONS.create}
              >
                <Button
                  // disabled={total >= 3}
                  type="primary"
                  onClick={() => navigate('/subscriptions/plans/details')}
                >
                  <PlusOutlined />
                  {t('subscription.create_plan')}
                </Button>
              </Private>
            ]
      }
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={results}
        loading={loading}
        pagination={false}
      />
    </PageWrapper>
  );
};

export default SubscriptionPlans;
